/* eslint-disable max-len */
import {PageIndexProps, RootNodeId, sideBarRootNode} from '@/domains/space/Repository/SidebarNew/type';
import {getSlotList} from '@/domains/space/Repository/SidebarNew/sidebarTreeUtils';
import {
    getRootNodeListData,
} from '@/domains/space/Repository/SidebarNew/sidebarTreeUtils/componentUtils';
import {useIntl} from 'react-intl';
import {useEffect, useState, memo, useRef, forwardRef, createRef, useMemo} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import SidebarTree from '@/domains/space/Repository/SidebarNew/components/SidebarTree';
import TabsItem from '@/domains/space/Repository/SidebarNew/components/rootItem';
import {SidebarSearch} from '@/domains/space/Repository/SidebarNew/components/SidebarSearch';
import {buildPath} from '@/config/routers';
import * as store from '@/store';
import {canEditInKnowledge} from '@/domains/space/Repository/knowledgeUtils';
import Icon, {IconSize} from '@/components/Icon';
import {SidebarAddSvg, SidebarRootAddSvg, TipsReferenceListSvg} from '@/icons/index';

import kuPiper from '@/middleware/kuPiper';
import classnames from 'classnames';
import './index.scss';
import sdk from '@/sdk';
import {clickRepoSettings, feLogger} from '@/buried';
import {isAdminRepository, isMemberRepository} from '@/store/util';
import SidebarSelectMore from './components/SidebarSelectMore';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';

const {
    useIsInGray: UseBatchOperate,
} = initGray(GRAY_RELEASE_KEYS.repoBatchOperate);

// 需要包裹一个forwardRef，让rc-tree内部虚拟列表计算对应高度
const RootNode = forwardRef((props: any, ref: any) => {
    const {
        item, curDocuments, canEdit, active, sidebarLoad, showDocumentSearch, onSelected, isDisable, isTop, repoPermissionRiskCount, isKuShareMode,
    } = props;
    const addBtnRef = useRef(null);
    const isBatchOperateGray = UseBatchOperate();
    const isToc = window.isTocPage;

    const isShowSelectAll = (isAdminRepository() || isMemberRepository()) && !isKuShareMode;

    function handleAddClick() {
        store.setSidebarMenuTarget({
            type: 'add',
            element: addBtnRef.current,
            isTreeRoot: true,
        });
    }

    return (
        <TabsItem
            ref={ref}
            sidebarLoad={sidebarLoad}
            curDocuments={curDocuments}
            key={item.id}
            id={`sideBar-nav-item-${item.id}`}
            data={item}
            className={classnames(
                item.id === RootNodeId.QACenter && 'sidebar-qa-center',
                item.id === RootNodeId.DocumentIndex && 'sidebar-document-index',
                [
                    RootNodeId.DocumentIndex, RootNodeId.CommonDoc,
                ].includes(item.id) && 'sidebar-no-hover',
                isTop && 'sidebar-top'
            )}
            isDisable={isDisable}
            active={active}
            onClick={onSelected}
            repoPermissionRiskCount={repoPermissionRiskCount}
            // TODO： canCreateDoc需要看下原来的逻辑
            slotRight={
                item.id === RootNodeId.DocumentIndex ? (
                    <>
                        {
                        // 目录项可能展示搜索，有知识库权限或者目录根节点大于1的时候展示搜索
                            !isToc && showDocumentSearch ? <SidebarSearch canCreateDoc={canEdit} /> : null
                        }
                        {
                        // 目录项可能展示搜索，有知识库权限或者目录根节点大于1的时候展示批量操作
                            !isToc && isShowSelectAll && isBatchOperateGray ? <SidebarSelectMore /> : null
                        }
                        {
                            !isToc && canEdit ? (
                                <button
                                    ref={addBtnRef}
                                    className="sidebar-root-create-button"
                                    onClick={handleAddClick}
                                >
                                    <Icon svg={SidebarRootAddSvg} size={IconSize.LG} noSpace />
                                </button>
                            ) : null
                        }
                    </>
                ) : null
            }
        />
    );
});

const RootPublicTip = forwardRef(({item}: any, ref: any) => {
    return (
        <div ref={ref} key={`${item.id}-tip`} className="sidebar-root-public-tip">
            <Icon svg={TipsReferenceListSvg} size={IconSize.SM} className="sidebar-root-public-tip-icon" />
            所有文档全公司可见
        </div>
    );
});

// 用户不可见，仅用于撑高底部区域 【批量操作的需求，需要撑高底部区域，防止被操作栏遮挡】
const PlaceholderSpacer = forwardRef(({height = 60}: { height?: number }, ref: any) => {
    return (
        <div
            ref={ref}
            style={{
                height: `${height}px`,
                visibility: 'hidden',
            }}
            className="placeholder-spacer"
        />
    );
});

const RootNodeContent = forwardRef(({contentRender, onLoad, sidebarLoad}: any, ref: any) => {
    return (
        // 需要有一个明确的dom用于rc-tree内部虚拟列表计算对应高度
        <div ref={ref}>
            {
                contentRender({
                    onLoad: onLoad,
                    sidebarLoad: sidebarLoad,
                })
            }
        </div>
    );
});

function Sidebar(props: PageIndexProps) {
    const intl = useIntl();
    const {
        isRecycleBin, isSetting, isHomeTag, isMemberManage,
        repository, docGuid,
        curDocument, curDocuments,
        documentOperate, repoPermissionRiskCount, isKuShareMode, isShowSidebar,
    } = props;
    const routerParams: any = useParams();
    const history = useHistory();

    const isBaikeRoute = store.getData(store.KEYS.IS_BAIKE_ROUTE);

    // 外部组件更新对应的根节点时可以用到此state
    const [sideBarSlotList, setSideBarSlotList] = useState([null, null]);
    const [sidebarLoad, setSidebarLoad] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>();
    const [rootNodeLoaded, setRootNodeLoaded] = useState(false);
    const [treeNodeLoaded, setTreeNodeLoaded] = useState(false);
    const canEdit = canEditInKnowledge(repository);
    const commonDocRef = createRef();

    // 获取初始节点数据 + 开放列表插槽
    const rootNodeListData = kuPiper.openListSlot(
        {
            scope: kuPiper.slotScopeMap.SpaceRepo,
            position: kuPiper.slotPositionMap.SpaceRepo.SidebarList,
            originList: getRootNodeListData(
                repository,
                {
                    isSetting,
                    isRecycleBin,
                    docGuid,
                    intl,
                    isMemberManage,
                    isHomeTag: isBaikeRoute ? !(routerParams.tagName || routerParams.name) : isHomeTag,
                }
            ),
        }
    );

    const [slotHeaderList, slotFooterList] = getSlotList(rootNodeListData, RootNodeId.DocumentIndex);

    // 业务有需要可以使用store.getSidebarListObj()?.scrollTo(id)调用
    function scrollTo(id: string) {
        const element = document.getElementById(`sideBar-nav-item-${id}`);
        if (element?.scrollIntoViewIfNeeded) {
            element?.scrollIntoViewIfNeeded();
        } else if (element?.scrollIntoView) {
            element?.scrollIntoView();
        }
    }

    useEffect(() => {
        const listObj = store.getSidebarListObj();
        store.setSidebarListObj({
            ...listObj,
            value: sideBarSlotList,
            setValue: setSideBarSlotList,
            scrollTo: scrollTo,
            selectNode: setSelectedItem,
        });
    }, [sideBarSlotList]);

    function goToLink(url: string | {
        docGuid: string;
        groupGuid: string;
        repositoryGuid: string;
        spaceGuid: string;
        path: string;
    }) {
        if (!url) {
            return;
        }
        if (typeof url === 'string') {
            history.push(url);
        } else {
            const {docGuid, groupGuid, repositoryGuid, spaceGuid, path} = url;
            const urlString = buildPath(path, {docGuid, groupGuid, repositoryGuid, spaceGuid});
            history.push(urlString);
        }
    }

    function handleSelectedItem(item : any) {
        setSelectedItem(item.id);
        // 解决目录树在设置项时，再次点击设置项导致的空白屏问题
        if (isSetting && item.id === RootNodeId.Setting) {
            return;
        }
        item.url && goToLink(item.url);
        if (item.id === RootNodeId.Setting) {
            feLogger(clickRepoSettings({repositoryId: repository.repositoryGuid}));
        }
    }

    useEffect(() => {
        const selectItem = rootNodeListData.find(item => {
            return item.isSelected || (routerParams.docGuid && routerParams.docGuid === item.id);
        });
        if (selectItem) {
            setSelectedItem(selectItem.id);
        } else {
            setSelectedItem(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, routerParams]);

    function handleRootNodeOnLoad(type: string) {
        if (type === 'commonDoc') {
            // 常用页面加载完成
            !rootNodeLoaded && setRootNodeLoaded(true);
        }
    }

    // 不展示常用标签的时候需要告知tree
    if (!(rootNodeListData.find(item => item.id === RootNodeId.CommonDoc)?.visible)) {
        handleRootNodeOnLoad(RootNodeId.CommonDoc);
    }

    function getRootNodeList(slotList: sideBarRootNode[], hasTop: boolean) {
        if (!repository) {
            return null;
        }
        const showDocumentSearch = (repository && repository.roleName) || (curDocuments && curDocuments.length > 0);
        const firstNode = slotList.find((item) => item.visible);

        const rel = slotList.reduce((rel: JSX.Element[], item) => {
            const isActive = selectedItem === item.id;
            item.visible && rel.push(
                <RootNode
                    key={item.id}
                    item={item}
                    curDocuments={curDocuments}
                    canEdit={canEdit}
                    active={isActive}
                    sidebarLoad={sidebarLoad}
                    showDocumentSearch={showDocumentSearch}
                    onSelected={handleSelectedItem}
                    isDisable={item.isDisable}
                    isTop={hasTop && item.id === firstNode?.id}
                    repoPermissionRiskCount={repoPermissionRiskCount}
                    isKuShareMode={isKuShareMode}
                />
            );
            item.id === RootNodeId.DocumentIndex && store.getCurRepository()?.scope === 11 && rel.push(
                <RootPublicTip item={item} />
            );
            item.visible && item.contentRender && rel.push(
                <RootNodeContent
                    key={`${item.id}-content`}
                    contentRender={item.contentRender}
                    onLoad={handleRootNodeOnLoad}
                    sidebarLoad={sidebarLoad}
                    ref={
                        item.id === 'commonDoc'
                            ? commonDocRef
                            : null
                    }
                />
            );
            return rel;
        }, []);

        if (hasTop === false) {
            rel.push(
                <PlaceholderSpacer />
            );
        }
        return rel;
    }

    function handleTreeLoad() {
        setTreeNodeLoaded(true);
    }

    useEffect(() => {
        // 常用列表数据加载完成及目录树数据首次节点定位完成则表示左侧栏渲染完成可以将目标元素展示到视图区域
        if (!sidebarLoad && rootNodeLoaded && treeNodeLoaded) {
            setSidebarLoad(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootNodeLoaded, treeNodeLoaded]);

    return (
        <div>
            {
                isShowSidebar ? <SidebarTree
                    slotHeader={
                        getRootNodeList(sideBarSlotList[0] || slotHeaderList, true)
                    }
                    slotFooter={
                        getRootNodeList(sideBarSlotList[1] || slotFooterList, false)
                    }
                    repository={repository}
                    curDocument={curDocument}
                    curDocuments={curDocuments}
                    onTreeLoad={handleTreeLoad}
                    sidebarLoad={sidebarLoad}
                    documentOperate={documentOperate}
                    docGuid={docGuid}
                    commonDocRef={commonDocRef}
                    curNodeIsRoot={
                        isRecycleBin || isSetting || (isBaikeRoute ? false : isHomeTag) || isMemberManage
                    }
                /> : null
            }

        </div>
    );
}

const PageIndexImpl = memo(store.connect([
    store.KEYS.CUR_DOCUMENT,
    store.KEYS.CUR_DOCUMENTS,
    store.KEYS.DOCUMENT_OPERATE,
    store.KEYS.IS_KU_SHARE_MODE,
    store.KEYS.MEMBERS_COUNT,
    store.KEYS.REPO_PERMISSION_RISK_COUNT,
    store.KEYS.IS_SHOW_SIDEBAR,
])(Sidebar));

export default PageIndexImpl;
